import React from 'react';
import Icon from '../../../components/AppIcon';

const HooperIndexDisplay = ({ 
  currentScore, 
  historicalAverage, 
  sleep, 
  fatigue, 
  soreness, 
  stress,
  className = "" 
}) => {
  const getScoreColor = (score) => {
    if (score <= 12) return 'text-success';
    if (score <= 16) return 'text-accent';
    if (score <= 20) return 'text-warning';
    return 'text-error';
  };

  const getScoreBackground = (score) => {
    if (score <= 12) return 'bg-success/10 border-success/20';
    if (score <= 16) return 'bg-accent/10 border-accent/20';
    if (score <= 20) return 'bg-warning/10 border-warning/20';
    return 'bg-error/10 border-error/20';
  };

  const getScoreStatus = (score) => {
    if (score <= 12) return { status: 'Excellent', description: 'Optimal wellness state' };
    if (score <= 16) return { status: 'Good', description: 'Good wellness state' };
    if (score <= 20) return { status: 'Fair', description: 'Monitor wellness closely' };
    return { status: 'Poor', description: 'Consider rest and recovery' };
  };

  const getComponentStatus = (value) => {
    if (value <= 2) return { label: 'Excellent', color: 'text-success' };
    if (value <= 4) return { label: 'Good', color: 'text-accent' };
    if (value <= 5) return { label: 'Fair', color: 'text-warning' };
    return { label: 'Poor', color: 'text-error' };
  };

  const getDifference = () => {
    if (!historicalAverage) return null;
    const diff = currentScore - historicalAverage;
    return {
      value: Math.abs(diff)?.toFixed(1),
      isHigher: diff > 0,
      isEqual: Math.abs(diff) < 0.1
    };
  };

  const scoreInfo = getScoreStatus(currentScore);
  const difference = getDifference();

  return (
    <div className={`bg-card rounded-lg p-6 border border-border ${className}`}>
      <div className="text-center mb-6">
        <h3 className="text-lg font-semibold text-text-primary mb-2">Hooper Index</h3>
        <div className={`inline-flex items-center justify-center w-32 h-32 rounded-full border-4 ${getScoreBackground(currentScore)}`}>
          <div className="text-center">
            <div className={`text-4xl font-bold ${getScoreColor(currentScore)}`}>
              {currentScore}
            </div>
            <div className="text-sm text-text-secondary">/ 28</div>
          </div>
        </div>
      </div>
      <div className="text-center mb-6">
        <div className={`inline-flex items-center space-x-2 px-4 py-2 rounded-full ${getScoreBackground(currentScore)}`}>
          <Icon 
            name={currentScore <= 12 ? 'CheckCircle' : currentScore <= 20 ? 'AlertCircle' : 'XCircle'} 
            size={16} 
            className={getScoreColor(currentScore)}
            strokeWidth={2}
          />
          <span className={`font-medium ${getScoreColor(currentScore)}`}>
            {scoreInfo?.status}
          </span>
        </div>
        <p className="text-sm text-text-secondary mt-2">
          {scoreInfo?.description}
        </p>
      </div>
      {/* Component Breakdown */}
      <div className="space-y-3 mb-6">
        <h4 className="text-sm font-medium text-text-primary">Component Breakdown</h4>
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col p-3 bg-muted rounded-lg">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center space-x-2">
                <Icon name="Moon" size={16} color="var(--color-accent)" strokeWidth={2} />
                <span className="text-sm text-text-secondary">Sleep</span>
              </div>
              <span className="text-sm font-medium text-text-primary">{sleep}</span>
            </div>
            <span className={`text-xs ${getComponentStatus(sleep)?.color}`}>
              {getComponentStatus(sleep)?.label}
            </span>
          </div>
          <div className="flex flex-col p-3 bg-muted rounded-lg">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center space-x-2">
                <Icon name="Battery" size={16} color="var(--color-warning)" strokeWidth={2} />
                <span className="text-sm text-text-secondary">Fatigue</span>
              </div>
              <span className="text-sm font-medium text-text-primary">{fatigue}</span>
            </div>
            <span className={`text-xs ${getComponentStatus(fatigue)?.color}`}>
              {getComponentStatus(fatigue)?.label}
            </span>
          </div>
          <div className="flex flex-col p-3 bg-muted rounded-lg">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center space-x-2">
                <Icon name="Zap" size={16} color="var(--color-error)" strokeWidth={2} />
                <span className="text-sm text-text-secondary">Soreness</span>
              </div>
              <span className="text-sm font-medium text-text-primary">{soreness}</span>
            </div>
            <span className={`text-xs ${getComponentStatus(soreness)?.color}`}>
              {getComponentStatus(soreness)?.label}
            </span>
          </div>
          <div className="flex flex-col p-3 bg-muted rounded-lg">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center space-x-2">
                <Icon name="Brain" size={16} color="var(--color-secondary)" strokeWidth={2} />
                <span className="text-sm text-text-secondary">Stress</span>
              </div>
              <span className="text-sm font-medium text-text-primary">{stress}</span>
            </div>
            <span className={`text-xs ${getComponentStatus(stress)?.color}`}>
              {getComponentStatus(stress)?.label}
            </span>
          </div>
        </div>
        <p className="text-xs text-text-secondary italic mt-2">
          Lower scores indicate better wellness state
        </p>
      </div>
      {/* Historical Comparison */}
      {historicalAverage && (
        <div className="pt-4 border-t border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Icon name="TrendingUp" size={16} color="var(--color-text-secondary)" strokeWidth={2} />
              <span className="text-sm text-text-secondary">vs 7-day average</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-text-primary">
                {historicalAverage?.toFixed(1)}
              </span>
              {difference && !difference?.isEqual && (
                <div className={`flex items-center space-x-1 ${
                  difference?.isHigher ? 'text-error' : 'text-success'
                }`}>
                  <Icon 
                    name={difference?.isHigher ? 'ArrowUp' : 'ArrowDown'} 
                    size={12} 
                    strokeWidth={2}
                  />
                  <span className="text-xs font-medium">{difference?.value}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HooperIndexDisplay;